/** Shared paiements helpers — Centre Activité HUB-C. */

export const PAIEMENTS_SECTIONS = {
  TIMELINE: 'timeline',
  SUMUP: 'sumup',
  STRIPE: 'stripe',
  PENDING: 'pending',
  PROOFS: 'proofs',
  RETRAITS: 'retraits',
};

export const SUMUP_STATUS_FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'pending', label: 'En attente' },
  { key: 'confirmed', label: 'Confirmés' },
  { key: 'cancelled', label: 'Annulés' },
];

export function parsePaymentDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isInDateRange(isoDate, dateDebut, dateFin) {
  if (!dateDebut && !dateFin) return true;
  const d = parsePaymentDate(isoDate);
  if (!d) return !dateDebut && !dateFin;
  const day = d.toISOString().slice(0, 10);
  if (dateDebut && day < dateDebut) return false;
  if (dateFin && day > dateFin) return false;
  return true;
}

export function matchesPaymentSearch(item, search, { kind = 'sumup' } = {}) {
  if (!search?.trim()) return true;
  const q = search.trim().toLowerCase();
  const haystack = [
    item.checkout_id,
    item.stripe_session_id,
    item.order_type,
    item.device_ref,
    item.order_id,
    item.confirmed_by,
    item.amount != null ? String(item.amount) : '',
    kind === 'proof' ? item.ref_commande : '',
    kind === 'proof' ? item.client_nom : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function sumupStatusMeta(status) {
  const map = {
    PAID_APPLIED: { label: 'Confirmé', badge: 'rm-badge rm-badge--success' },
    PAID_PENDING: { label: 'En attente', badge: 'rm-badge rm-badge--muted' },
    PENDING: { label: 'En cours', badge: 'rm-badge rm-badge--muted' },
    CANCELLED: { label: 'Annulé', badge: 'rm-badge rm-badge--danger' },
  };
  return map[status] || { label: status || '—', badge: 'rm-badge rm-badge--danger' };
}

export function stripeStatusMeta(status) {
  const map = {
    COMPLETED: { label: 'Payé', badge: 'rm-badge rm-badge--success' },
    PENDING: { label: 'En attente', badge: 'rm-badge rm-badge--muted' },
    CANCELLED: { label: 'Annulé', badge: 'rm-badge rm-badge--danger' },
  };
  return map[status] || { label: status || '—', badge: 'rm-badge rm-badge--muted' };
}

export function getSumupOrderLabel(payment) {
  if (payment.order_type === 'repair') return `Réparation ${payment.device_ref || ''}`.trim();
  if (payment.order_type === 'boutique') return 'Commande boutique';
  if (payment.order_type === 'pos_v2') {
    const magasin = payment.magasin_id ? ` · ${payment.magasin_id}` : '';
    return `Vente POS${magasin}`;
  }
  return payment.order_type || 'Paiement SumUp';
}

export function getSumupPendingDetailLine(payment) {
  const parts = [];
  if (payment.description && payment.order_type === 'pos_v2') parts.push(payment.description);
  if (payment.vendeur) parts.push(payment.vendeur);
  if (payment.device_ref) parts.push(payment.device_ref);
  if (payment.order_id) parts.push(payment.order_id);
  if (payment.checkout_id) parts.push(payment.checkout_id.slice(0, 12));
  return parts.filter(Boolean).join(' · ');
}

export function getStripeOrderLabel(payment) {
  if (payment.order_type === 'repair') return `Réparation ${payment.device_ref || ''}`.trim();
  if (payment.order_type === 'devis_deplacement') return `Devis déplacement ${payment.device_ref || ''}`.trim();
  if (payment.order_type === 'boutique') return 'Commande boutique';
  return payment.order_type || 'Paiement Stripe';
}

export function filterSumupByStatus(payments, statusKey) {
  if (statusKey === 'pending') {
    return payments.filter((p) => ['PAID_PENDING', 'PENDING'].includes(p.status));
  }
  if (statusKey === 'confirmed') return payments.filter((p) => p.status === 'PAID_APPLIED');
  if (statusKey === 'cancelled') return payments.filter((p) => p.status === 'CANCELLED');
  return payments;
}

export function buildTimelineEvents({ sumupPayments = [], stripePayments = [], limit = 100 } = {}) {
  const events = [];

  sumupPayments.forEach((p) => {
    events.push({
      id: `sumup-${p.checkout_id}`,
      source: 'sumup',
      date: p.updated_at || p.created_at,
      payment: p,
    });
  });

  stripePayments.forEach((p) => {
    events.push({
      id: `stripe-${p.stripe_session_id || p.id}`,
      source: 'stripe',
      date: p.updated_at || p.created_at,
      payment: p,
    });
  });

  return events
    .filter((e) => e.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, limit);
}

export function formatPaymentDate(value) {
  const d = parsePaymentDate(value);
  if (!d) return '—';
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function activitePaiementsLinkFromFilters(filters) {
  const params = new URLSearchParams({ tab: 'paiements' });
  if (filters?.search) params.set('search', filters.search);
  if (filters?.vendeur) params.set('vendeur', filters.vendeur);
  if (filters?.date_debut) params.set('date_debut', filters.date_debut);
  if (filters?.date_fin) params.set('date_fin', filters.date_fin);
  if (filters?.magasin_id) params.set('magasin_id', filters.magasin_id);
  if (filters?.period) params.set('period', filters.period);
  return `/activite?${params.toString()}`;
}
